'use client';

import { useState, forwardRef } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { BRAND_BLUE } from '@voley/shared-types/constants';

/** Shared color tokens for onboarding form inputs. */
const INPUT_COLORS = {
  text: 'var(--color-text-primary)',
  label: 'var(--color-text-primary)',
  muted: 'var(--color-text-tertiary)',
  background: 'var(--color-card-background-header)',
  border: 'var(--color-border)',
  borderFocus: BRAND_BLUE,
  error: 'var(--color-error)',
} as const;

// ── FormInput ────────────────────────────────────────────────────────────────

interface FormInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  icon?: React.ElementType;
  error?: string;
}

export const FormInput = forwardRef<HTMLInputElement, FormInputProps>(
  function FormInput({ label, icon: Icon, error, ...props }, ref) {
    const [isFocused, setIsFocused] = useState(false);

    return (
      <div>
        {label && (
          <label
            htmlFor={props.id}
            className="block text-sm font-medium mb-1.5"
            style={{ color: INPUT_COLORS.label }}
          >
            {label}
          </label>
        )}
        <div className="relative">
          {Icon && (
            <div
              className="absolute left-3 top-1/2 -translate-y-1/2"
              style={{ color: INPUT_COLORS.muted }}
            >
              <Icon size={18} />
            </div>
          )}
          <input
            ref={ref}
            {...props}
            onFocus={(e) => {
              setIsFocused(true);
              props.onFocus?.(e);
            }}
            onBlur={(e) => {
              setIsFocused(false);
              props.onBlur?.(e);
            }}
            className="w-full px-4 py-2.5 rounded-xl text-base transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
            style={{
              backgroundColor: INPUT_COLORS.background,
              border: `1px solid ${error ? INPUT_COLORS.error : isFocused ? INPUT_COLORS.borderFocus : INPUT_COLORS.border}`,
              color: INPUT_COLORS.text,
              paddingLeft: Icon ? '2.5rem' : undefined,
              outline: 'none',
              boxShadow: isFocused
                ? `0 0 0 3px ${error ? 'rgba(239, 68, 68, 0.1)' : 'rgba(53, 129, 207, 0.1)'}`
                : 'none',
            }}
          />
        </div>
        {error && (
          <p className="mt-1.5 text-xs" style={{ color: INPUT_COLORS.error }}>
            {error}
          </p>
        )}
      </div>
    );
  },
);

// ── PasswordInput ────────────────────────────────────────────────────────────

interface PasswordInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string;
  icon?: React.ElementType;
  error?: string;
  hint?: string;
}

export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  function PasswordInput({ label, icon: Icon, error, hint, ...props }, ref) {
    const [showPassword, setShowPassword] = useState(false);
    const [isFocused, setIsFocused] = useState(false);

    return (
      <div>
        {label && (
          <label
            htmlFor={props.id}
            className="block text-sm font-medium mb-1.5"
            style={{ color: INPUT_COLORS.label }}
          >
            {label}
          </label>
        )}
        <div className="relative">
          {Icon && (
            <div
              className="absolute left-3 top-1/2 -translate-y-1/2"
              style={{ color: INPUT_COLORS.muted }}
            >
              <Icon size={18} />
            </div>
          )}
          <input
            ref={ref}
            {...props}
            type={showPassword ? 'text' : 'password'}
            onFocus={(e) => {
              setIsFocused(true);
              props.onFocus?.(e);
            }}
            onBlur={(e) => {
              setIsFocused(false);
              props.onBlur?.(e);
            }}
            className="w-full py-2.5 rounded-xl text-base transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
            style={{
              backgroundColor: INPUT_COLORS.background,
              border: `1px solid ${error ? INPUT_COLORS.error : isFocused ? INPUT_COLORS.borderFocus : INPUT_COLORS.border}`,
              color: INPUT_COLORS.text,
              paddingLeft: Icon ? '2.5rem' : '1rem',
              paddingRight: '2.5rem',
              outline: 'none',
              boxShadow: isFocused
                ? `0 0 0 3px ${error ? 'rgba(239, 68, 68, 0.1)' : 'rgba(53, 129, 207, 0.1)'}`
                : 'none',
            }}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
            style={{ color: INPUT_COLORS.muted }}
            onMouseEnter={(e) => { e.currentTarget.style.color = INPUT_COLORS.label; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = INPUT_COLORS.muted; }}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
        {error ? (
          <p className="mt-1.5 text-xs" style={{ color: INPUT_COLORS.error }}>{error}</p>
        ) : hint ? (
          <p className="mt-1.5 text-xs" style={{ color: INPUT_COLORS.muted }}>{hint}</p>
        ) : null}
      </div>
    );
  },
);
